import React, { useEffect, useState } from "react";
import { Wordmark } from "@/components/Logo";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Download, Palette, Type, Globe, Sun, Moon, Package, Map } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { PageLoader } from "@/components/FindoSpinner";

const LANG_NAMES = { fr: "Français", en: "English", es: "Español", de: "Deutsch", it: "Italiano", pt: "Português" };
const LANG_FLAG = { fr: "🇫🇷", en: "🇬🇧", es: "🇪🇸", de: "🇩🇪", it: "🇮🇹", pt: "🇵🇹" };

const COLORS = [
  { name: "Navy", nameKey: null, hex: "#0A1628" },
  { name: "Emerald", nameKey: null, hex: "#00C896" },
  { name: "Blanc", nameKey: "pk.colorWhite", hex: "#FFFFFF" },
];

export default function PressKit() {
  const { t } = useLang();
  const [assets, setAssets] = useState(null);
  const [langs, setLangs] = useState(["fr"]);
  const [lang, setLang] = useState("fr");
  const [variant, setVariant] = useState("dark");
  const [scene, setScene] = useState("plain");

  useEffect(() => { document.title = "Kit presse — Findloop"; }, []);
  useEffect(() => {
    fetch(`${API}/branding/assets`)
      .then((r) => r.json())
      .then((d) => { setAssets(d.assets); setLangs(d.languages || ["fr"]); })
      .catch(() => setAssets([]));
  }, []);

  const fileUrl = (a, forceParams = false) => {
    const params = [];
    if (a.tagline || forceParams) params.push(`lang=${lang}`);
    if (a.variant || forceParams) params.push(`variant=${variant}`);
    if (a.scene || forceParams) params.push(`scene=${scene}`);
    return `${API}/branding/file/${a.slug}${params.length ? "?" + params.join("&") : ""}`;
  };

  const kit = (assets || []).find((a) => a.slug === "findloop-brand-kit");
  const items = (assets || []).filter((a) => a.slug !== "findloop-brand-kit");

  return (
    <div className="bg-background min-h-screen" data-testid="press-kit-page">
      {/* Hero */}
      <div className="relative bg-findo-navy text-white overflow-hidden">
        <div className="absolute inset-0 opacity-30" style={{ background: "radial-gradient(700px 340px at 85% -10%, #00C896, transparent)" }} />
        <div className="relative max-w-5xl mx-auto px-6 py-16">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3.5 py-1.5 text-xs font-bold uppercase tracking-wide">
            <Package size={14} /> {t("pk.pressKit")}
          </span>
          <h1 className="mt-4 font-display text-4xl md:text-5xl font-semibold" data-testid="press-kit-title">{t("pub.brandReady")}</h1>
          <p className="mt-3 text-white/80 max-w-2xl text-base">
            {t("pk.heroDesc")}
          </p>
          {kit && (
            <a href={`${API}/branding/file/${kit.slug}?lang=${lang}&variant=${variant}&scene=${scene}`} download className="inline-block mt-6" data-testid="press-download-zip">
              <Button className="rounded-xl font-semibold bg-white text-findo-navy hover:bg-white/90 h-11 px-6">
                <Download size={16} className="mr-2" /> {t("pk.downloadZip")}
              </Button>
            </a>
          )}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-12 space-y-14">
        {/* Controls */}
        <section className="flex flex-wrap items-end gap-6" data-testid="press-controls">
          <div>
            <label className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-500 mb-2"><Globe size={14} /> {t("pk.taglineLang")}</label>
            <div className="flex flex-wrap gap-2" data-testid="press-lang-group">
              {langs.map((c) => (
                <button key={c} onClick={() => setLang(c)} data-testid={`press-lang-${c}`}
                  className={`rounded-full px-3.5 py-2 text-sm font-semibold border transition-colors ${lang === c ? "bg-findo-navy text-white border-findo-navy" : "bg-white text-slate-600 border-border hover:border-findo-navy/40"}`}>
                  {LANG_FLAG[c]} {LANG_NAMES[c] || c}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-500 mb-2"><Palette size={14} /> {t("pk.variant")}</label>
            <div className="inline-flex rounded-full border border-border bg-white p-1" data-testid="press-variant-group">
              <button onClick={() => setVariant("dark")} data-testid="press-variant-dark"
                className={`rounded-full px-4 py-1.5 text-sm font-semibold flex items-center gap-1.5 transition-colors ${variant === "dark" ? "bg-findo-navy text-white" : "text-slate-600"}`}>
                <Moon size={14} /> {t("pk.darkBg")}
              </button>
              <button onClick={() => setVariant("light")} data-testid="press-variant-light"
                className={`rounded-full px-4 py-1.5 text-sm font-semibold flex items-center gap-1.5 transition-colors ${variant === "light" ? "bg-findo-navy text-white" : "text-slate-600"}`}>
                <Sun size={14} /> {t("pk.lightBg")}
              </button>
            </div>
          </div>
          <div>
            <label className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-500 mb-2"><Map size={14} /> {t("pub.scene")}</label>
            <div className="inline-flex rounded-full border border-border bg-white p-1" data-testid="press-scene-group">
              <button onClick={() => setScene("plain")} data-testid="press-scene-plain"
                className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${scene === "plain" ? "bg-findo-navy text-white" : "text-slate-600"}`}>
                {t("pk.plain")}
              </button>
              <button onClick={() => setScene("map")} data-testid="press-scene-map"
                className={`rounded-full px-4 py-1.5 text-sm font-semibold flex items-center gap-1.5 transition-colors ${scene === "map" ? "bg-findo-navy text-white" : "text-slate-600"}`}>
                <Map size={14} /> {t("pk.mapCollect")}
              </button>
            </div>
          </div>
        </section>

        {/* Colors */}
        <section data-testid="press-colors">
          <h2 className="font-display text-2xl font-semibold text-findo-navy mb-5 flex items-center gap-2"><Palette size={20} /> {t("pk.brandColors")}</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 gap-4">
            {COLORS.map((c) => (
              <div key={c.hex} className="rounded-2xl border border-border overflow-hidden card-shadow" data-testid={`press-color-${c.name.toLowerCase()}`}>
                <div className="h-24" style={{ background: c.hex, borderBottom: c.hex === "#FFFFFF" ? "1px solid #e5e7eb" : "none" }} />
                <div className="p-3">
                  <p className="font-semibold text-slate-800 text-sm">{c.nameKey ? t(c.nameKey) : c.name}</p>
                  <p className="text-xs text-slate-400 uppercase">{c.hex}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Typography */}
        <section data-testid="press-typography">
          <h2 className="font-display text-2xl font-semibold text-findo-navy mb-5 flex items-center gap-2"><Type size={20} /> {t("pk.typography")}</h2>
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="rounded-2xl border border-border p-5 card-shadow bg-white">
              <p className="text-4xl" style={{ color: "#0A1628" }}><Wordmark /></p>
              <p className="mt-3 text-sm font-semibold text-slate-800">Poppins Regular + Bold</p>
              <p className="text-xs text-slate-400">{t("pk.logoWordmark")}</p>
            </div>
            <div className="rounded-2xl border border-border p-5 card-shadow bg-white">
              <p className="font-display text-3xl font-semibold text-findo-navy">{t("pk.headings")}</p>
              <p className="mt-3 text-sm font-semibold text-slate-800">Fraunces</p>
              <p className="text-xs text-slate-400">{t("pub.editorialTitles")}</p>
            </div>
            <div className="rounded-2xl border border-border p-5 card-shadow bg-white">
              <p className="text-2xl text-slate-700">{t("pk.bodyText")}</p>
              <p className="mt-3 text-sm font-semibold text-slate-800">Manrope</p>
              <p className="text-xs text-slate-400">{t("pk.bodyCopy")}</p>
            </div>
          </div>
        </section>

        {/* Assets */}
        <section data-testid="press-assets">
          <h2 className="font-display text-2xl font-semibold text-findo-navy mb-5 flex items-center gap-2"><Download size={20} /> {t("pk.logosVisuals")}</h2>
          {assets === null ? (
            <PageLoader label={t("c.loading")} className="py-8" size={32} />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="press-assets-grid">
              {items.map((a) => (
                <div key={a.slug} className="bg-white rounded-2xl border border-border p-4 card-shadow flex flex-col" data-testid={`press-card-${a.slug}`}>
                  <div className={`rounded-xl border border-border grid place-items-center overflow-hidden mb-3 ${a.slug.includes("transparent") ? "bg-[repeating-conic-gradient(#eef2f7_0%_25%,#fff_0%_50%)] bg-[length:20px_20px]" : "bg-slate-50"}`} style={{ aspectRatio: `${a.width} / ${a.height}`, minHeight: 96 }}>
                    <img key={`${a.slug}-${lang}-${variant}-${scene}`} src={fileUrl(a)} alt={a.label} className="max-w-full max-h-44 object-contain" />
                  </div>
                  <p className="font-semibold text-slate-800 text-sm">{a.label}</p>
                  <p className="text-xs text-slate-400 mb-3 uppercase">{a.width} × {a.height} px · {a.ext}</p>
                  <a href={fileUrl(a)} download className="mt-auto" data-testid={`press-download-${a.slug}`}>
                    <Button size="sm" className="w-full rounded-xl font-semibold">
                      <Download size={14} className="mr-1.5" /> {t("pk.download")}
                    </Button>
                  </a>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
